import { useState } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import { Phone, Mail, Menu, X, Facebook, Instagram, Music2, Clock, MapPin } from "lucide-react";
import Logo from "@/components/Logo";

export const PHONE = "07740051799";
export const PHONE_TEL = "tel:07740051799";
export const NAP_NAME = "Mobile Kitchen Company (UK) Limited";
export const NAP_ADDRESS = "Ground Floor, Egerton House, 68 Baker Street, Weybridge, Surrey, KT13 8AL";

const NAV = [
  { to: "/", label: "Home", slug: "home" },
  { to: "/whats-included", label: "What's Included", slug: "whats-included" },
  { to: "/how-it-works", label: "How It Works", slug: "how-it-works" },
  { to: "/gallery", label: "Gallery", slug: "gallery" },
  { to: "/about", label: "About", slug: "about" },
  { to: "/testimonials", label: "Testimonials", slug: "testimonials" },
  { to: "/service-areas", label: "Service Areas", slug: "service-areas" },
  { to: "/contact", label: "Contact", slug: "contact" },
];

const SOCIALS = [
  { href: "https://www.facebook.com/mobilekitchencompany", label: "Facebook", Icon: Facebook, slug: "facebook" },
  { href: "https://www.instagram.com/mobilekitchencompany", label: "Instagram", Icon: Instagram, slug: "instagram" },
  { href: "https://www.tiktok.com/@mobilekitchencompany", label: "TikTok", Icon: Music2, slug: "tiktok" },
];

function TopBar() {
  return (
    <div className="bg-[#1A237E] text-white text-sm">
      <div className="max-w-7xl mx-auto px-4 flex flex-wrap items-center justify-between gap-2 py-2">
        <div className="flex items-center gap-2 text-slate-200">
          <Clock className="h-4 w-4 text-white" />
          <span>Monday – Friday, 09:00 – 17:00</span>
        </div>
        <a
          href={PHONE_TEL}
          data-testid="topbar-call-button"
          className="flex items-center gap-2 font-semibold hover:text-[#C5CAE9] transition-colors"
        >
          <Phone className="h-4 w-4 text-white" />
          Call us: {PHONE}
        </a>
      </div>
    </div>
  );
}

function Header() {
  const [open, setOpen] = useState(false);
  const linkClass = ({ isActive }) =>
    `px-3 py-2 text-sm font-medium rounded transition-colors ${
      isActive ? "text-[#1A237E] bg-slate-100" : "text-slate-600 hover:text-[#1A237E] hover:bg-slate-50"
    }`;
  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 flex items-center justify-between gap-4 py-3">
        <Logo />
        <nav className="hidden lg:flex items-center gap-1" data-testid="main-nav">
          {NAV.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.to === "/"} className={linkClass} data-testid={`nav-link-${item.slug}`}>
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          <a
            href={PHONE_TEL}
            data-testid="header-call-button"
            className="flex items-center gap-2 px-4 py-2 border border-[#1A237E] text-[#1A237E] font-semibold text-sm rounded hover:bg-[#1A237E] hover:text-white transition-colors"
          >
            <Phone className="h-4 w-4" /> {PHONE}
          </a>
          <Link
            to="/contact"
            data-testid="header-quote-button"
            className="px-4 py-2 bg-[#1A237E] text-white font-semibold text-sm rounded hover:bg-[#121A5C] transition-colors"
          >
            Get a Free Quote
          </Link>
        </div>
        <button
          className="lg:hidden p-2 text-[#1A237E]"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open && (
        <nav className="lg:hidden border-t border-slate-200 bg-white px-4 py-3 flex flex-col gap-1" data-testid="mobile-nav">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              onClick={() => setOpen(false)}
              className={linkClass}
              data-testid={`mobile-nav-link-${item.slug}`}
            >
              {item.label}
            </NavLink>
          ))}
          <div className="flex gap-3 pt-3">
            <a href={PHONE_TEL} data-testid="mobile-call-button" className="flex-1 text-center px-4 py-2 border border-[#1A237E] text-[#1A237E] font-semibold text-sm rounded">
              Call {PHONE}
            </a>
            <Link to="/contact" onClick={() => setOpen(false)} data-testid="mobile-quote-button" className="flex-1 text-center px-4 py-2 bg-[#1A237E] text-white font-semibold text-sm rounded">
              Get a Free Quote
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="bg-[#141956] text-slate-300" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 py-12 grid gap-10 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <Logo dark />
          <p className="mt-4 text-sm leading-relaxed text-slate-400">
            Surrey's trusted specialists in mobile kitchen hire and temporary kitchen hire, keeping you in
            your home during renovations and building works.
          </p>
          <div className="flex gap-3 mt-5">
            {SOCIALS.map(({ href, label, Icon, slug }) => (
              <a
                key={slug}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                data-testid={`social-link-${slug}`}
                className="h-9 w-9 flex items-center justify-center rounded border border-slate-600 text-slate-300 hover:text-white hover:border-white transition-colors"
              >
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>
        <div>
          <h3 className="text-white text-base font-bold mb-4">Quick Links</h3>
          <ul className="space-y-2 text-sm">
            {NAV.map((item) => (
              <li key={item.to}>
                <Link to={item.to} className="hover:text-[#C5CAE9] transition-colors" data-testid={`footer-link-${item.slug}`}>
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h3 className="text-white text-base font-bold mb-4">Contact Us</h3>
          <address className="not-italic text-sm space-y-3">
            <p className="flex gap-2">
              <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-[#C5CAE9]" />
              <span data-testid="footer-nap">
                {NAP_NAME}
                <br />
                {NAP_ADDRESS}
              </span>
            </p>
            <p>
              <a href={PHONE_TEL} className="flex gap-2 hover:text-[#C5CAE9] transition-colors" data-testid="footer-call-button">
                <Phone className="h-4 w-4 mt-0.5 shrink-0 text-[#C5CAE9]" />
                {PHONE}
              </a>
            </p>
            <p>
              <a href="mailto:enquiries@mobilekitchencompany.co.uk" className="flex gap-2 hover:text-[#C5CAE9] transition-colors" data-testid="footer-email-link">
                <Mail className="h-4 w-4 mt-0.5 shrink-0 text-[#C5CAE9]" />
                enquiries@mobilekitchencompany.co.uk
              </a>
            </p>
          </address>
        </div>
        <div>
          <h3 className="text-white text-base font-bold mb-4">Opening Hours</h3>
          <ul className="text-sm space-y-2">
            <li className="flex justify-between gap-4"><span>Monday – Friday</span><span>09:00 – 17:00</span></li>
            <li className="flex justify-between gap-4"><span>Saturday</span><span>Closed</span></li>
            <li className="flex justify-between gap-4"><span>Sunday</span><span>Closed</span></li>
          </ul>
          <Link
            to="/contact"
            data-testid="footer-quote-button"
            className="inline-block mt-5 px-5 py-2 bg-white text-[#1A237E] text-sm font-semibold rounded hover:bg-slate-100 transition-colors"
          >
            Get a Free Quote
          </Link>
        </div>
      </div>
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-5 text-xs text-slate-500 flex flex-wrap justify-between gap-2">
          <span data-testid="footer-copyright">© {new Date().getFullYear()} Mobile Kitchen Company (UK) Limited. All rights reserved.</span>
          <span>Mobile kitchen hire Surrey · Temporary kitchen hire Surrey</span>
        </div>
      </div>
    </footer>
  );
}

export default function PublicLayout() {
  return (
    <div className="min-h-screen flex flex-col">
      <TopBar />
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
